import React, { useCallback, useMemo, useState } from "react";
import { Button } from "antd";
import moment from "moment";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from "recharts";
import FilterBar from "components/filterBar";
import {
  KpiRow,
  KpiTile,
  CoverageNote,
  ChartCard,
  EmptyState,
  fmt,
  delta,
} from "components/analytics";
import { useManpowerTrend, useSiteComparison } from "hooks/analytics/analytics";
import { exportRowsToXlsx } from "helpers/exportXlsx";

/**
 * Manpower trend.
 *
 * The gap this closes: the product held a year of attendance history but every
 * screen showed a single day of it. This one takes a range, and can lay the
 * previous equal-length window underneath for comparison.
 */

const COLOR = { current: "#1e5dfe", previous: "#94a3b8", contractors: "#6d47d9" };

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="an-tip">
      <div className="an-tip-title">{moment(label).format("ddd, DD MMM YYYY")}</div>
      {payload.map((item) => (
        <div className="an-tip-row" key={item.dataKey}>
          <span>{item.name}</span>
          <span>{fmt(item.value)}</span>
        </div>
      ))}
    </div>
  );
};

const ManpowerTrend = () => {
  const [filters, setFilters] = useState(null);
  const { mutate: loadTrend, data: trendRes, isLoading } = useManpowerTrend();
  const { mutate: loadSites, data: siteRes } = useSiteComparison();

  const onApply = useCallback(
    (nextFilters) => {
      setFilters(nextFilters);
      loadTrend(nextFilters);
      loadSites(nextFilters);
    },
    [loadTrend, loadSites]
  );

  const data = trendRes?.data;
  const summary = data?.summary;
  const previous = data?.previous?.summary;
  const sites = siteRes?.data?.sites || [];

  /**
   * Aligns the previous period onto the same x-axis by index, so the two windows
   * can be read against each other even when the months differ in length.
   */
  const chartData = useMemo(() => {
    const series = data?.series || [];
    const prevSeries = data?.previous?.series || [];
    return series.map((point, index) => ({
      date: point.date,
      headcount: point.headcount,
      previousHeadcount: prevSeries[index]?.headcount ?? null,
      previousDate: prevSeries[index]?.date ?? null,
    }));
  }, [data]);

  const peak = useMemo(
    () => (data?.series || []).reduce((max, p) => Math.max(max, p.headcount || 0), 0),
    [data]
  );

  const downloadExcel = () => {
    const rows = [["Date", "Day", "Headcount", "Man-days", "Contractors", "Measured records", "Avg hours"]];
    (data?.series || []).forEach((p) =>
      rows.push([p.date, p.dayOfWeek, p.headcount, p.records, p.contractors, p.measured, p.avgHours ?? ""])
    );
    exportRowsToXlsx(rows, "Manpower_Trend", "Manpower Trend");
  };

  const hasData = (data?.series || []).length > 0;

  return (
    <div className="an-page">
      <FilterBar
        onApply={onApply}
        title={
          <>
            <h1>Manpower trend</h1>
            <p>Daily headcount over any date range</p>
          </>
        }
        actions={
          <Button type="primary" className="csv" disabled={!hasData} onClick={downloadExcel}>
            Download Excel
          </Button>
        }
      />

      <KpiRow>
        <KpiTile
          label="Man-days"
          tone="primary"
          value={fmt(summary?.manDays)}
          change={delta(summary?.manDays, previous?.manDays)}
          foot="Total attendance records"
        />
        <KpiTile
          label="People on site"
          value={fmt(summary?.people)}
          change={delta(summary?.people, previous?.people)}
          foot="Distinct employees in range"
        />
        <KpiTile
          label="Avg daily strength"
          value={fmt(summary?.avgDailyHeadcount, 1)}
          change={delta(summary?.avgDailyHeadcount, previous?.avgDailyHeadcount)}
          foot="Man-days per active day"
        />
        <KpiTile label="Peak day" value={fmt(peak)} foot="Highest single-day headcount" />
        <KpiTile
          label="Contractors"
          value={fmt(summary?.contractors)}
          change={delta(summary?.contractors, previous?.contractors)}
          foot="Firms supplying labour"
        />
        <KpiTile
          label="Measured hours"
          value={fmt(summary?.actualHours, 1)}
          foot="From records with a real checkout"
        />
      </KpiRow>

      <CoverageNote coverage={data?.coverage} />

      <ChartCard
        title="Daily headcount"
        subtitle={
          data
            ? `${moment(data.from).format("DD MMM YYYY")} – ${moment(data.to).format(
                "DD MMM YYYY"
              )}${data.previous ? " · dashed line is the previous period, aligned by day index" : ""}`
            : undefined
        }
        help="Each point is the number of distinct people who tapped in that day."
      >
        {hasData ? (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 18, bottom: 4, left: -8 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => moment(d).format("DD MMM")}
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                stroke="#eaecf0"
                minTickGap={24}
              />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} stroke="#eaecf0" allowDecimals={false} />
              <RTooltip content={<TrendTooltip />} />
              <Legend
                verticalAlign="top"
                height={30}
                iconType="plainline"
                wrapperStyle={{ fontSize: 12.5, color: "#667085" }}
              />
              {data?.previous && (
                <Line
                  type="monotone"
                  dataKey="previousHeadcount"
                  name="Previous period"
                  stroke={COLOR.previous}
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={false}
                  connectNulls
                />
              )}
              <Area
                type="monotone"
                dataKey="headcount"
                name="Headcount"
                stroke={COLOR.current}
                strokeWidth={2}
                fill={COLOR.current}
                fillOpacity={0.12}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState loading={isLoading} hasFilters={!!filters} />
        )}
      </ChartCard>

      <ChartCard
        title="By site"
        subtitle="Man-days and average daily strength for each site in the selected range."
      >
        {sites.length ? (
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="num">People</th>
                  <th className="num">Man-days</th>
                  <th className="num">Days active</th>
                  <th className="num">Avg daily strength</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr key={site.siteId}>
                    <td>{site.siteName}</td>
                    <td className="num">{fmt(site.people)}</td>
                    <td className="num">{fmt(site.manDays)}</td>
                    <td className="num">{fmt(site.daysPresent)}</td>
                    <td className="num">{fmt(site.avgDailyStrength, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState loading={isLoading} hasFilters={!!filters} />
        )}
      </ChartCard>
    </div>
  );
};

export default ManpowerTrend;
